import type { ReactNode } from "react";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { AuthSplash } from "./AuthSplash";
import { gaps } from "../../../primitives/spacing";
import { riseIn } from "./auth_motion";

interface AuthLayoutProps {
  /** The form, or any content, shown in the form column. */
  children: ReactNode;
  /** A block shown under the form, e.g. the waiting-list form. */
  extra?: ReactNode;
  /** Replaces the default branded splash on the side panel. */
  splash?: ReactNode;
}

const SplitLayout = styled(Stack)({
  minHeight: "100dvh",
});

const SplashColumn = styled(Stack)(({ theme }) => ({
  display: "none",
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.palette.primary.main,
  [theme.breakpoints.up("md")]: {
    display: "flex",
    width: "45%",
    flexShrink: 0,
    padding: theme.spacing(5),
  },
  [theme.breakpoints.up("lg")]: {
    width: "42%",
    padding: theme.spacing(7),
  },
}));

const FormColumn = styled("main")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4, 2),
}));

const FormColumnContent = styled(Stack)({
  width: "100%",
  maxWidth: 400,
  ...riseIn(0.7, 0.1),
});

/**
 * Split-screen layout for every authentication screen: content on one side, a branded splash on
 * the other (dropped on small screens). Sign-in, password reset and a future sign-up share it.
 */
export function AuthLayout({ children, extra, splash }: AuthLayoutProps) {
  return (
    <SplitLayout direction="row">
      <SplashColumn aria-hidden>{splash ?? <AuthSplash />}</SplashColumn>
      <FormColumn>
        <FormColumnContent gap={gaps.base}>
          {children}
          {extra}
        </FormColumnContent>
      </FormColumn>
    </SplitLayout>
  );
}
